import { Provider } from '@fruitful/types';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Sheet,
    Spinner,
    TrashIcon,
    useToastController,
    YStack,
} from 'smoothie';

import useDeleteQuilttConnection from '../../holistic/hooks/useDeleteQuilttConnection';
import useGetHolisticSummary from '../../holistic/hooks/useGetHolisticSummary';
import useToggleAccountVisibility from '../../holistic/hooks/useToggleAccountVisibility';
import {
    holisticAccountsAtom,
    holisticSummaryAtom,
} from '../../holistic/state/HolisticAtoms';
import { captureException } from '../../logger/Logger';

export enum RemoveSheetPage {
    SELECT = 'select',
    CONFIRM_REMOVE = 'confirmRemove',
}

interface RemoveBankSheetProps {
    isOpen: boolean;
    connectionId: string;
    institutionName: string;
    onClose: () => void;
}

export const RemoveBankSheet = ({
    isOpen,
    connectionId,
    institutionName,
    onClose,
}: RemoveBankSheetProps) => {
    const toast = useToastController();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const { refetch: refetchHolisticSummary } = useGetHolisticSummary(false);
    const holisticAccounts = useAtomValue(holisticAccountsAtom);
    const setHolisticSummary = useSetAtom(holisticSummaryAtom);

    const { mutateAsync: deleteConnection, isPending: isDeleting } =
        useDeleteQuilttConnection();
    const { mutateAsync: toggleAccountVisibility } =
        useToggleAccountVisibility();

    const [removeSheetPage, setRemoveSheetPage] = React.useState(
        RemoveSheetPage.SELECT,
    );

    if (isDeleting) {
        return <></>;
    }

    const onSubmitDeleteBank = async (
        connectionId: string,
        institutionName: string,
    ) => {
        try {
            setIsSubmitting(true);

            if (connectionId !== '') {
                await deleteConnection({ body: { connectionId } });
            }

            const manualAccounts = holisticAccounts.filter(
                (account) =>
                    account.provider === Provider.MANUAL &&
                    account.institutionName === institutionName,
            );

            if (manualAccounts.length) {
                await Promise.all(
                    manualAccounts.map(
                        async (manualAccount) =>
                            await toggleAccountVisibility({
                                body: {
                                    accountId: manualAccount.id,
                                    provider: manualAccount.provider,
                                    isHidden: true,
                                },
                            }),
                    ),
                );
            }

            setHolisticSummary((prev) => {
                if (!prev) return prev;

                const updated = { ...prev };
                (Object.keys(updated) as Array<keyof typeof updated>).forEach(
                    (key) => {
                        const category = updated[key];
                        updated[key] = {
                            ...category,
                            accounts: category.accounts.filter(
                                (a) =>
                                    a.connectionId !== connectionId &&
                                    !(
                                        a.provider === Provider.MANUAL &&
                                        a.institutionName === institutionName
                                    ),
                            ),
                        };
                    },
                );
                return updated;
            });
        } catch (e) {
            captureException(e);
            toast.show('Error removing bank', { duration: 2000 });
        } finally {
            setIsSubmitting(false);
        }
    };

    let content: React.JSX.Element;
    let sheetTitle: string;

    switch (removeSheetPage) {
        case RemoveSheetPage.CONFIRM_REMOVE:
            sheetTitle = 'Remove institution';
            content = (
                <ConfirmRemove
                    onConfirmRemove={async () => {
                        await onSubmitDeleteBank(connectionId, institutionName);
                        void refetchHolisticSummary();
                        onClose();
                    }}
                    onGoBack={() => {
                        onClose();
                        setRemoveSheetPage(RemoveSheetPage.SELECT);
                    }}
                    isSubmitting={isSubmitting}
                />
            );
            break;
        case RemoveSheetPage.SELECT:
        default:
            sheetTitle = 'Bank options';
            content = (
                <SelectOption
                    onClose={() => {
                        onClose();
                        setRemoveSheetPage(RemoveSheetPage.SELECT);
                    }}
                    onPressRemove={() => {
                        setRemoveSheetPage(RemoveSheetPage.CONFIRM_REMOVE);
                    }}
                />
            );
    }

    return (
        <Sheet
            onClose={onClose}
            title={sheetTitle}
            showCloseButton={false}
            isOpen={isOpen}
        >
            {content}
        </Sheet>
    );
};

interface SelectOptionProps {
    onClose: () => void;
    onPressRemove: () => void;
}

const SelectOption = ({ onPressRemove }: SelectOptionProps) => {
    return (
        <YStack marginTop={24} gap={8} width="100%">
            <Button
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.SMALL}
                text="Remove bank"
                icon={TrashIcon}
                onPress={onPressRemove}
                tone="danger"
            />
        </YStack>
    );
};

interface ConfirmRemoveProps {
    onConfirmRemove: () => Promise<void>;
    onGoBack: () => void;
    isSubmitting: boolean;
}

const ConfirmRemove = ({
    onConfirmRemove,
    onGoBack,
    isSubmitting,
}: ConfirmRemoveProps) => {
    if (isSubmitting) {
        return (
            <YStack
                width="100%"
                alignItems="center"
                justifyContent="center"
                marginVertical={32}
            >
                <Spinner />
            </YStack>
        );
    }

    return (
        <YStack marginTop={16} width="100%">
            <BodyMediumRegular textAlign="center" color="$gray1100">
                Are you sure you want to remove this institution? This will
                remove the institution and all associated accounts within.
            </BodyMediumRegular>
            <YStack marginTop={24} gap={8} width="100%">
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Yes, remove institution and accounts"
                    onPress={onConfirmRemove}
                    tone="danger"
                />
                <Button
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.LARGE}
                    text="Cancel"
                    onPress={onGoBack}
                />
            </YStack>
        </YStack>
    );
};
